import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  FileText, 
  Home, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  X,
  UserPlus,
  ArrowLeftRight,
  Eye,
  Edit,
  Trash2,
  UserX,
  UserCheck
} from 'lucide-react';

// Mevcut akademik dönemi hesapla (Eylül'den itibaren yeni dönem)
const getCurrentSemester = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-12
  
  // Eylül (9) ve sonrası yeni dönem başlar
  if (month >= 9) {
    return `${year}/${year + 1}`;
  } else {
    return `${year - 1}/${year}`;
  }
};

// Gelecek 5 yıl için dönem listesi oluştur
const generateSemesterOptions = () => {
  const options = [];
  const currentYear = new Date().getFullYear();
  
  for (let i = -2; i <= 3; i++) {
    const year1 = currentYear + i;
    const year2 = year1 + 1;
    options.push(`${year1}/${year2}`);
  }
  
  return options;
};

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSemester, setSelectedSemester] = useState(getCurrentSemester()); // Varsayılan olarak mevcut dönem
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showChangeRoom, setShowChangeRoom] = useState(false);
  const [showRoomDetails, setShowRoomDetails] = useState(false);
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [roomDetails, setRoomDetails] = useState(null);
  const [editingStudent, setEditingStudent] = useState({
    name: '',
    username: '',
    email: '',
    tc: '',
    department: '',
    grade: '',
    password: '',
    semester: ''
  });
  const [newRoom, setNewRoom] = useState({
    roomNumber: '',
    floor: '',
    capacity: ''
  });
  const [newStudent, setNewStudent] = useState({
    name: '',
    username: '',
    password: '',
    email: '',
    tc: '',
    department: '',
    grade: '',
    semester: getCurrentSemester() // Varsayılan olarak mevcut dönem
  });


  const fetchData = async () => {
    try {
      // Cache'i temizlemek için timestamp ekle
      const timestamp = new Date().getTime();
      const random = Math.random();
      const cacheHeaders = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      
      const [applicationsRes, roomsRes, studentsRes, statsRes, allStudentsRes] = await Promise.all([
        axios.get(`/api/applications/all?t=${timestamp}&r=${random}`, { headers: cacheHeaders }),
        axios.get(`/api/rooms?t=${timestamp}&r=${random}`, { headers: cacheHeaders }),
        axios.get(`/api/admin/students?semester=${selectedSemester}&t=${timestamp}&r=${random}`, { headers: cacheHeaders }),
        axios.get(`/api/admin/statistics?t=${timestamp}&r=${random}`, { headers: cacheHeaders }),
        axios.get(`/api/applications/all-students?t=${timestamp}&r=${random}`, { headers: cacheHeaders })
      ]);

      setApplications(applicationsRes.data);
      setRooms(roomsRes.data);
      setStudents(studentsRes.data);
      setStats(statsRes.data);
      setAllStudents(allStudentsRes.data);
    } catch (error) {
      console.error('Veriler alınamadı:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedSemester]); // Dönem değiştiğinde verileri yenile

  const handleApplicationAction = async (applicationId, status) => {
    try {
      await axios.put(`/api/applications/${applicationId}/status`, { status });
      toast.success(`Başvuru ${status === 'approved' ? 'onaylandı' : 'reddedildi'}`);
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/rooms', newRoom);
      toast.success('Oda başarıyla eklendi');
      setShowAddRoom(false);
      setNewRoom({ roomNumber: '', floor: '', capacity: '' });
      
      // Verileri yenile
      await fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Oda eklenemedi';
      toast.error(message);
      console.error('Oda ekleme hatası:', error);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/students', newStudent);
      toast.success('Öğrenci başarıyla eklendi');
      setShowAddStudent(false);
      setNewStudent({ name: '', username: '', password: '', email: '', tc: '', department: '', grade: '', semester: getCurrentSemester() });
      
      // Verileri yenile
      await fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Öğrenci eklenemedi';
      toast.error(message);
      console.error('Öğrenci ekleme hatası:', error);
    }
  };

  const handleChangeStudentRoom = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/api/applications/change-student-room', {
        studentId: selectedStudent.id,
        newRoomId: selectedStudent.newRoomId
      });
      toast.success('Öğrenci odası başarıyla değiştirildi');
      setShowChangeRoom(false);
      setSelectedStudent(null);
      
      // Verileri yenile
      await fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Oda değiştirme başarısız';
      toast.error(message);
      console.error('Oda değiştirme hatası:', error);
    }
  };

  const handleViewRoomDetails = async (room) => {
    try {
      const response = await axios.get(`/api/rooms/admin/${room.id}`);
      setRoomDetails(response.data);
      setShowRoomDetails(true);
    } catch (error) {
      toast.error('Oda detayları alınamadı');
    }
  };

  const handleSendCredentials = async (studentId) => {
    try {
      const response = await axios.post(`/api/admin/students/${studentId}/send-credentials`);
      if (response.data.emailSent) {
        toast.success('Kullanıcı bilgileri email ile gönderildi');
      } else {
        toast.error('Email gönderilemedi: ' + response.data.emailError);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Email gönderilemedi';
      toast.error(message);
    }
  };

  const handleEditStudent = (student) => {
    setEditingStudent({
      name: student.name,
      username: student.username,
      email: student.email || '',
      tc: student.tc || '',
      department: student.department,
      grade: student.grade,
      password: '',
      semester: student.semester || getCurrentSemester()
    });
    setSelectedStudent(student);
    setShowEditStudent(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/admin/students/${selectedStudent.id}`, editingStudent);
      toast.success('Öğrenci başarıyla güncellendi');
      setShowEditStudent(false);
      setSelectedStudent(null);
      setEditingStudent({ name: '', username: '', email: '', tc: '', department: '', grade: '', password: '', semester: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Öğrenci güncellenemedi';
      toast.error(message);
    }
  };

  const handleDeleteStudent = async () => {
    try {
      await axios.delete(`/api/admin/students/${selectedStudent.id}`);
      toast.success('Öğrenci başarıyla silindi');
      setShowDeleteConfirm(false);
      setSelectedStudent(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Öğrenci silinemedi';
      toast.error(message);
    }
  };

  const handleToggleStudentStatus = async (student) => {
    try {
      const newStatus = student.is_active === 0 ? 1 : 0;
      await axios.put(`/api/admin/students/${student.id}/status`, { is_active: newStatus });
      toast.success(`Öğrenci ${newStatus === 1 ? 'aktif' : 'pasif'} yapıldı`);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'İşlem başarısız';
      toast.error(message);
    }
  };

  const handleUpdateRoomCapacity = async (roomId, newCapacity) => {
    try {
      await axios.put(`/api/admin/rooms/${roomId}`, { capacity: parseInt(newCapacity) });
      toast.success('Oda kapasitesi başarıyla güncellendi');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.error || 'Kapasite güncellenemedi';
      toast.error(message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
        return 'Onaylandı';
      case 'rejected':
        return 'Reddedildi';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto !py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto !py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Yönetim Paneli
        </h1>
        <p className="text-gray-600">
          Yurt yönetimi için tüm işlemleri buradan gerçekleştirebilirsiniz.
        </p>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Home className="w-6 h-6 text-primary" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats?.totalRooms}</div>
              <div className="text-sm text-gray-600">Toplam Oda</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats?.totalStudents}</div>
              <div className="text-sm text-gray-600">Toplam Öğrenci</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats?.pendingApplications}</div>
              <div className="text-sm text-gray-600">Bekleyen Başvuru</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{stats?.occupiedRooms}</div>
              <div className="text-sm text-gray-600">Dolu Oda</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Başvurular
            </button>
            <button
              onClick={() => setActiveTab('rooms')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rooms'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Oda Yönetimi
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Öğrenci Yönetimi
            </button>
            <button
              onClick={() => setActiveTab('room-changes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'room-changes'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Oda Değişiklikleri
            </button>
          </nav>
        </div>
      </div>

      {/* Başvurular Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Başvuru Yönetimi</h2>
          </div>

          {applications.length === 0 ? (
            <div className="card text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <div className="text-xl font-medium text-gray-900 mb-2">
                Henüz başvuru yok
              </div>
              <p className="text-gray-600">
                Öğrenci başvuruları burada görüntülenecek.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((application) => (
                <div
                  key={application.id}
                  className="card"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      {getStatusIcon(application.status)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {application.name}
                          </h3>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                            {getStatusText(application.status)}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-gray-600">Bölüm</div>
                            <div className="font-medium">{application.department}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Sınıf</div>
                            <div className="font-medium">{application.grade}. Sınıf</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Başvuru Oda</div>
                            <div className="font-medium">Oda {application.room_number}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Başvuru Tarihi</div>
                            <div className="font-medium">
                              {new Date(application.created_at).toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {application.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApplicationAction(application.id, 'approved')}
                          className="btn btn-success"
                        >
                          Onayla
                        </button>
                        <button
                          onClick={() => handleApplicationAction(application.id, 'rejected')}
                          className="btn btn-danger"
                        >
                          Reddet
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Oda Yönetimi Tab */}
      {activeTab === 'rooms' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Oda Yönetimi</h2>
            <button
              onClick={() => setShowAddRoom(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Oda Ekle
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="card"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Oda {room.room_number}
                    </h3>
                    <p className="text-sm text-gray-600">{room.floor}. Kat</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    room.available_beds > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {room.available_beds > 0 ? 'Müsait' : 'Dolu'}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Kapasite:</span>
                    <select
                      value={room.capacity}
                      onChange={(e) => handleUpdateRoomCapacity(room.id, e.target.value)}
                      className="text-sm py-1 px-1.5 w-16 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                      title="Kapasiteyi değiştirmek için seçin"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                    </select>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Dolu:</span>
                    <span className="font-medium">{room.occupied} Kişi</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Boş:</span>
                    <span className="font-medium">{room.available_beds} Yatak</span>
                  </div>
                </div>

                {room.occupants_info && room.occupants_info.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="text-sm font-medium text-gray-900 mb-2">Oda Sakinleri:</div>
                    <div className="space-y-1">
                      {room.occupants_info.map((occupant, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          • {occupant}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4 mt-4">
                  <button
                    onClick={() => handleViewRoomDetails(room)}
                    className="btn btn-secondary w-full text-sm"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Detayları Gör
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Öğrenci Yönetimi Tab */}
      {activeTab === 'students' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Öğrenci Yönetimi</h2>
            <div className="flex items-center space-x-4">
              {/* Dönem Filtresi */}
              <div className="flex items-center space-x-2">
                <label htmlFor="semesterFilter" className="text-sm font-medium text-gray-700">
                  Dönem:
                </label>
                <select
                  id="semesterFilter"
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  className="form-select text-sm"
                >
                  {generateSemesterOptions().map((sem) => (
                    <option key={sem} value={sem}>
                      {sem}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowAddStudent(true)}
                className="btn btn-primary"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Yeni Öğrenci Ekle
              </button>
            </div>
          </div>

          {students.length === 0 ? (
            <div className="card text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <div className="text-xl font-medium text-gray-900 mb-2">
                Henüz öğrenci yok
              </div>
              <p className="text-gray-600">
                Yeni öğrenci ekleyerek başlayın.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {students.map((student) => (
                <div
                  key={student.id}
                  className="card"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-primary-100 rounded-lg">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {student.name}
                          </h3>
                          <div className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {student.role === 'admin' ? 'Yönetici' : 'Öğrenci'}
                          </div>
                          {student.is_active === 0 && (
                            <div className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Pasif
                            </div>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-gray-600">Kullanıcı Adı</div>
                            <div className="font-medium">{student.username}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">E-posta</div>
                            <div className="font-medium">{student.email || 'Belirtilmemiş'}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Bölüm</div>
                            <div className="font-medium">{student.department}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Sınıf</div>
                            <div className="font-medium">{student.grade}. Sınıf</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Dönem</div>
                            <div className="font-medium">{student.semester || getCurrentSemester()}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSendCredentials(student.id)}
                            className="btn btn-secondary text-sm"
                            disabled={!student.email}
                            title={!student.email ? 'Email adresi bulunamadı' : 'Kullanıcı bilgilerini email ile gönder'}
                          >
                            📧 Bilgileri Gönder
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* İşlem İkonları */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditStudent(student)}
                        className="p-2 text-primary hover:bg-primary-100 rounded-lg transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleToggleStudentStatus(student)}
                        className={`p-2 rounded-lg transition-colors ${
                          student.is_active === 0 
                            ? 'text-green-600 hover:bg-green-100' 
                            : 'text-orange-600 hover:bg-orange-100'
                        }`}
                        title={student.is_active === 0 ? 'Aktif Yap' : 'Pasif Yap'}
                      >
                        {student.is_active === 0 ? (
                          <UserCheck className="w-5 h-5" />
                        ) : (
                          <UserX className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Oda Değişiklikleri Tab */}
      {activeTab === 'room-changes' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Oda Değişiklikleri</h2>
            <button
              onClick={() => setShowChangeRoom(true)}
              className="btn btn-primary"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              Öğrenci Odası Değiştir
            </button>
          </div>

          {allStudents.length === 0 ? (
            <div className="card text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <div className="text-xl font-medium text-gray-900 mb-2">
                Henüz öğrenci yok
              </div>
              <p className="text-gray-600">
                Öğrenci listesi burada görüntülenecek.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {allStudents.map((student) => (
                <div
                  key={student.id}
                  className="card"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-primary-100 rounded-lg">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {student.name}
                          </h3>
                          <div className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {student.department} • {student.grade}. Sınıf
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-gray-600">Mevcut Oda</div>
                            <div className="font-medium">
                              {student.room_number ? `Oda ${student.room_number} (${student.floor}. Kat)` : 'Oda atanmamış'}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Yerleşim Tarihi</div>
                            <div className="font-medium">
                              {student.joined_at ? new Date(student.joined_at).toLocaleDateString('tr-TR') : '-'}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">İşlem</div>
                            <button
                              onClick={() => {
                                setSelectedStudent({
                                  id: student.id,
                                  name: student.name,
                                  currentRoom: student.room_number,
                                  newRoomId: ''
                                });
                                setShowChangeRoom(true);
                              }}
                              className="btn btn-secondary text-sm"
                            >
                              Oda Değiştir
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Yeni Oda Ekleme Modal */}
      {showAddRoom && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Yeni Oda Ekle</h2>
              <button
                onClick={() => setShowAddRoom(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddRoom} className="space-y-4">
              <div className="form-group">
                <label htmlFor="roomNumber" className="form-label">Oda Numarası</label>
                <input
                  type="text"
                  id="roomNumber"
                  className="form-input"
                  value={newRoom.roomNumber}
                  onChange={(e) => setNewRoom({...newRoom, roomNumber: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="floor" className="form-label">Kat</label>
                <select
                  id="floor"
                  className="form-select"
                  value={newRoom.floor}
                  onChange={(e) => setNewRoom({...newRoom, floor: e.target.value})}
                  required
                >
                  <option value="">Kat seçin</option>
                  <option value="1">1. Kat</option>
                  <option value="2">2. Kat</option>
                  <option value="3">3. Kat</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="capacity" className="form-label">Kapasite</label>
                <select
                  id="capacity"
                  className="form-select"
                  value={newRoom.capacity}
                  onChange={(e) => setNewRoom({...newRoom, capacity: e.target.value})}
                  required
                >
                  <option value="">Kapasite seçin</option>
                  <option value="1">1 Kişilik</option>
                  <option value="2">2 Kişilik</option>
                  <option value="3">3 Kişilik</option>
                  <option value="4">4 Kişilik</option>
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                >
                  Oda Ekle
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddRoom(false)}
                  className="btn btn-secondary flex-1"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Yeni Öğrenci Ekleme Modal */}
      {showAddStudent && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Yeni Öğrenci Ekle</h2>
              <button
                onClick={() => setShowAddStudent(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddStudent} className="space-y-4">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Ad Soyad</label>
                <input
                  type="text"
                  id="name"
                  className="form-input"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="tc" className="form-label">TC Kimlik No</label>
                <input
                  type="text"
                  id="tc"
                  className="form-input"
                  value={newStudent.tc}
                  onChange={(e) => setNewStudent({...newStudent, tc: e.target.value})}
                  maxLength="11"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="username" className="form-label">Kullanıcı Adı</label>
                <input
                  type="text"
                  id="username"
                  className="form-input"
                  value={newStudent.username}
                  onChange={(e) => setNewStudent({...newStudent, username: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Şifre</label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">E-posta</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="department" className="form-label">Bölüm</label>
                <select
                  id="department"
                  className="form-select"
                  value={newStudent.department}
                  onChange={(e) => setNewStudent({...newStudent, department: e.target.value})}
                  required
                >
                  <option value="">Bölüm seçin</option>
                  <option value="İlahiyat">İlahiyat</option>
                  <option value="Tıp">Tıp</option>
                  <option value="Hukuk">Hukuk</option>
                  <option value="Mühendislik">Mühendislik</option>
                  <option value="Eğitim">Eğitim</option>
                  <option value="Fen-Edebiyat">Fen-Edebiyat</option>
                  <option value="İktisadi ve İdari Bilimler">İktisadi ve İdari Bilimler</option>
                  <option value="Sağlık Bilimleri">Sağlık Bilimleri</option>
                  <option value="Diğer">Diğer</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="grade" className="form-label">Sınıf</label>
                <select
                  id="grade"
                  className="form-select"
                  value={newStudent.grade}
                  onChange={(e) => setNewStudent({...newStudent, grade: e.target.value})}
                  required
                >
                  <option value="">Sınıf seçin</option>
                  <option value="1">1. Sınıf</option>
                  <option value="2">2. Sınıf</option>
                  <option value="3">3. Sınıf</option>
                  <option value="4">4. Sınıf</option>
                  <option value="5">5. Sınıf</option>
                  <option value="6">6. Sınıf</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="semester" className="form-label">Dönem</label>
                <select
                  id="semester"
                  className="form-select"
                  value={newStudent.semester}
                  onChange={(e) => setNewStudent({...newStudent, semester: e.target.value})}
                  required
                >
                  {generateSemesterOptions().map((sem) => (
                    <option key={sem} value={sem}>
                      {sem}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Varsayılan: {getCurrentSemester()} (Mevcut dönem)
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                >
                  Öğrenci Ekle
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddStudent(false)}
                  className="btn btn-secondary flex-1"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Oda Değiştirme Modal */}
      {showChangeRoom && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Öğrenci Odası Değiştir</h2>
              <button
                onClick={() => {
                  setShowChangeRoom(false);
                  setSelectedStudent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedStudent && (
              <form onSubmit={handleChangeStudentRoom} className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Öğrenci</label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium">{selectedStudent.name}</div>
                    <div className="text-sm text-gray-600">
                      Mevcut Oda: {selectedStudent.currentRoom || 'Oda atanmamış'}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newRoom" className="form-label">Yeni Oda</label>
                  <select
                    id="newRoom"
                    className="form-select"
                    value={selectedStudent.newRoomId}
                    onChange={(e) => setSelectedStudent({...selectedStudent, newRoomId: e.target.value})}
                    required
                  >
                    <option value="">Oda seçin</option>
                    {rooms
                      .filter(room => room.available_beds > 0 || room.id === selectedStudent.currentRoomId)
                      .map((room) => (
                        <option key={room.id} value={room.id}>
                          Oda {room.room_number} ({room.floor}. Kat) - {room.available_beds} boş yatak
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="btn btn-primary flex-1"
                  >
                    Oda Değiştir
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangeRoom(false);
                      setSelectedStudent(null);
                    }}
                    className="btn btn-secondary flex-1"
                  >
                    İptal
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Oda Detayları Modal (Admin için) */}
      {showRoomDetails && roomDetails && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Oda {roomDetails.room_number} Detayları (Admin)
              </h2>
              <button
                onClick={() => {
                  setShowRoomDetails(false);
                  setRoomDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Oda Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Oda Numarası</div>
                  <div className="font-medium">{roomDetails.room_number}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Kat</div>
                  <div className="font-medium">{roomDetails.floor}. Kat</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Kapasite</div>
                  <div className="font-medium">{roomDetails.capacity} Kişi</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Dolu</div>
                  <div className="font-medium">{roomDetails.occupied} Kişi</div>
                </div>
              </div>

              {/* Oda Sakinleri (Admin için isimler dahil) */}
              {roomDetails.occupants && roomDetails.occupants.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Oda Sakinleri ({roomDetails.occupants.length}/{roomDetails.capacity})
                  </h3>
                  <div className="space-y-3">
                    {roomDetails.occupants.map((occupant, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-4 bg-primary-50 rounded-lg border border-primary-200"
                      >
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{occupant.name}</div>
                          <div className="text-sm text-gray-600">{occupant.displayText}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {index + 1}. Kişi
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {roomDetails.occupants && roomDetails.occupants.length === 0 && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div className="text-lg font-medium text-gray-900 mb-2">
                    Oda boş
                  </div>
                  <p className="text-sm text-gray-600">
                    Bu odada henüz öğrenci bulunmuyor.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Öğrenci Düzenleme Modal */}
      {showEditStudent && selectedStudent && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Öğrenci Düzenle</h2>
              <button
                onClick={() => {
                  setShowEditStudent(false);
                  setSelectedStudent(null);
                  setEditingStudent({ name: '', username: '', email: '', tc: '', department: '', grade: '', password: '', semester: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateStudent} className="space-y-4">
              <div className="form-group">
                <label htmlFor="editName" className="form-label">Ad Soyad</label>
                <input
                  type="text"
                  id="editName"
                  className="form-input"
                  value={editingStudent.name}
                  onChange={(e) => setEditingStudent({...editingStudent, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editTc" className="form-label">TC Kimlik No</label>
                <input
                  type="text"
                  id="editTc"
                  className="form-input"
                  value={editingStudent.tc}
                  onChange={(e) => setEditingStudent({...editingStudent, tc: e.target.value})}
                  maxLength="11"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editUsername" className="form-label">Kullanıcı Adı</label>
                <input
                  type="text"
                  id="editUsername"
                  className="form-input"
                  value={editingStudent.username}
                  onChange={(e) => setEditingStudent({...editingStudent, username: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editPassword" className="form-label">Şifre (Değiştirmek için doldurun)</label>
                <input
                  type="password"
                  id="editPassword"
                  className="form-input"
                  value={editingStudent.password}
                  onChange={(e) => setEditingStudent({...editingStudent, password: e.target.value})}
                  placeholder="Boş bırakılırsa değişmez"
                />
              </div>

              <div className="form-group">
                <label htmlFor="editEmail" className="form-label">E-posta</label>
                <input
                  type="email"
                  id="editEmail"
                  className="form-input"
                  value={editingStudent.email}
                  onChange={(e) => setEditingStudent({...editingStudent, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editDepartment" className="form-label">Bölüm</label>
                <select
                  id="editDepartment"
                  className="form-select"
                  value={editingStudent.department}
                  onChange={(e) => setEditingStudent({...editingStudent, department: e.target.value})}
                  required
                >
                  <option value="">Bölüm seçin</option>
                  <option value="İlahiyat">İlahiyat</option>
                  <option value="Tıp">Tıp</option>
                  <option value="Hukuk">Hukuk</option>
                  <option value="Mühendislik">Mühendislik</option>
                  <option value="Eğitim">Eğitim</option>
                  <option value="Fen-Edebiyat">Fen-Edebiyat</option>
                  <option value="İktisadi ve İdari Bilimler">İktisadi ve İdari Bilimler</option>
                  <option value="Sağlık Bilimleri">Sağlık Bilimleri</option>
                  <option value="Diğer">Diğer</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editGrade" className="form-label">Sınıf</label>
                <select
                  id="editGrade"
                  className="form-select"
                  value={editingStudent.grade}
                  onChange={(e) => setEditingStudent({...editingStudent, grade: e.target.value})}
                  required
                >
                  <option value="">Sınıf seçin</option>
                  <option value="1">1. Sınıf</option>
                  <option value="2">2. Sınıf</option>
                  <option value="3">3. Sınıf</option>
                  <option value="4">4. Sınıf</option>
                  <option value="5">5. Sınıf</option>
                  <option value="6">6. Sınıf</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editSemester" className="form-label">Dönem</label>
                <select
                  id="editSemester"
                  className="form-select"
                  value={editingStudent.semester}
                  onChange={(e) => setEditingStudent({...editingStudent, semester: e.target.value})}
                  required
                >
                  {generateSemesterOptions().map((sem) => (
                    <option key={sem} value={sem}>
                      {sem}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                >
                  Güncelle
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditStudent(false);
                    setSelectedStudent(null);
                    setEditingStudent({ name: '', username: '', email: '', tc: '', department: '', grade: '', password: '' });
                  }}
                  className="btn btn-secondary flex-1"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteConfirm && selectedStudent && (
        <div className="modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Öğrenci Sil</h2>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedStudent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                <strong>{selectedStudent.name}</strong> adlı öğrenciyi silmek istediğinizden emin misiniz?
              </p>
              <p className="text-sm text-red-600">
                Bu işlem geri alınamaz. Öğrencinin tüm başvuruları ve oda ilişkileri silinecektir.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteStudent}
                className="btn btn-danger flex-1"
              >
                Evet, Sil
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedStudent(null);
                }}
                className="btn btn-secondary flex-1"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 